import React, { useContext, useState } from 'react';
import { withI18n, withI18nProps } from '@lingui/react';
import cx from 'classnames';
import { DocumentLink } from '@qover/molecules';
import { BrokerContext } from '@qover/contexts';
import {
  Container,
  Header,
  InnerContainer,
  ListDocs,
  ListQuestions,
  Question,
  RightContainer,
} from './faq.styles';
import messages from './faq.messages';
import { ContractIcon } from './ContractIcon';
import { LogsIcon } from './LogsIcon';

const defaultDocuments = [
  {
    'data-test': 'faq.insuranceProductInformationLink',
    analyticsLabel: 'IPID',
    label: messages.doc1,
    type: 'IPID',
  },
  {
    'data-test': 'faq.generalPolicyLink',
    analyticsLabel: 'General Policy Conditions',
    label: messages.doc2,
    type: 'generalConditions',
  },
];

const analytics = {
  pushFullFaq: () =>
    // @ts-ignore
    dataLayer.push({
      event: 'faqEvent',
      eventCategory: 'FAQ',
      eventAction: 'See All FAQ',
    }),
  pushQuestion: (questionId: string, questionText: string) =>
    // @ts-ignore
    dataLayer.push({
      event: 'faqEvent',
      eventCategory: 'FAQ',
      eventLabel: questionId,
      eventAction: questionText,
    }),
  pushDocument: (documentLabel: string) =>
    // @ts-ignore
    dataLayer.push({
      event: 'documentEvent',
      eventCategory: 'Document',
      eventAction: 'Open Document',
      eventLabel: documentLabel,
    }),
};

type Faq = withI18nProps & {
  className?: string;
  documents?: Array<{
    'data-test': string;
    type: string;
    analyticsLabel: string;
    label: React.ReactElement;
  }>;
  complaintLink?: string;
  conflictsLink?: string;
  hideFullFaq?: boolean;
  initialOpenedQuestion?: number;
  legalNotice?: string;
  questions?: Array<{
    Title: React.ReactElement;
    Answer: React.ReactElement;
  }>;
};

const NewTabLink = (p: any) => (
  <a {...p} target="_blank" rel="noopener noreferrer" />
);

const FaqI18n: React.FC<Faq> = props => {
  const broker = useContext(BrokerContext);

  const fullFaqLink = broker?.informations?.links?.fullFaq;
  const privacyPolicyLink =
    broker?.informations?.links?.privacy ??
    'https://www.qover.com/terms-and-policies#parentVerticalTab4';

  const {
    documents = defaultDocuments,
    complaintLink = 'https://qover.com/terms-policies/complaints',
    conflictsLink = 'https://qover.com/terms-policies/conflicts',
  } = props;

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(
    props.initialOpenedQuestion != null ? props.initialOpenedQuestion : null,
  );

  function toggleQuestion(index: number) {
    if (currentQuestionIndex === index) {
      setCurrentQuestionIndex(null);
    } else {
      setCurrentQuestionIndex(index);
    }
  }

  const { questions, i18n } = props;
  return (
    <Container className={cx(props.className, { row: questions?.length })}>
      <InnerContainer>
        <Header>{messages.title}</Header>
        <InnerContainer className={!questions?.length ? 'row' : ''}>
          <ListDocs>
            {documents.map(document => (
              <li key={document.type}>
                <ContractIcon />
                <DocumentLink
                  data-test={document['data-test']}
                  type={document.type}
                  onClick={() =>
                    analytics.pushDocument(document.analyticsLabel)
                  }
                >
                  {document.label}
                </DocumentLink>
              </li>
            ))}
            <li>
              <ContractIcon />
              <NewTabLink
                data-test="faq.privacyPolicyLink"
                href={privacyPolicyLink}
                onClick={() => analytics.pushDocument('Privacy policy')}
              >
                {messages.doc3}
              </NewTabLink>
            </li>
          </ListDocs>
          <ListDocs>
            <li className="outside-link">
              <LogsIcon />
              <NewTabLink
                data-test="faq.complaintsLink"
                href={complaintLink}
                onClick={() => analytics.pushDocument('Complaints')}
              >
                {messages.complaints}
              </NewTabLink>
            </li>
            <li className="outside-link">
              <LogsIcon />
              <NewTabLink
                data-test="faq.conflictsOfInterestPolicyLink"
                href={conflictsLink}
                onClick={() =>
                  analytics.pushDocument('ConflictsOfInterestPolicy')
                }
              >
                {messages.conflictsOfInterestPolicy}
              </NewTabLink>
            </li>
            {!props.hideFullFaq && (
              <li className="outside-link">
                <LogsIcon />
                <NewTabLink
                  data-test="faq.fullFaqLink"
                  href={fullFaqLink}
                  onClick={analytics.pushFullFaq}
                >
                  {messages.fullFaqLink}
                </NewTabLink>
              </li>
            )}
            {props.legalNotice && (
              <li className="outside-link">
                <LogsIcon />
                <NewTabLink
                  data-test="faq.legalNoticePolicyLink"
                  href={props.legalNotice}
                  onClick={() => analytics.pushDocument('LegalNotice')}
                >
                  {messages.legalNotice}
                </NewTabLink>
              </li>
            )}
          </ListDocs>
        </InnerContainer>
      </InnerContainer>
      {!!questions?.length && (
        <RightContainer>
          <ListQuestions>
            {questions.map(({ Title, Answer }, index) => (
              <li
                key={index}
                className={cx({
                  active: currentQuestionIndex === index,
                })}
              >
                <Question
                  className={cx({
                    active: currentQuestionIndex === index,
                  })}
                  role="button"
                  onClick={() => {
                    if (currentQuestionIndex !== index) {
                      const questionId = Title.props.id;
                      const questionText = i18n._(questionId);
                      analytics.pushQuestion(questionId, questionText);
                    }
                    toggleQuestion(index);
                  }}
                  data-test={`faq.questions.${index + 1}`}
                  tabIndex={index}
                >
                  {Title}
                </Question>
                <div className="content" data-test={`faq.answers.${index + 1}`}>
                  {currentQuestionIndex === index && Answer}
                </div>
              </li>
            ))}
          </ListQuestions>
        </RightContainer>
      )}
    </Container>
  );
};

export const Faq = withI18n()(FaqI18n);
